import {
  Badge,
  Box,
  Button,
  CopyButton,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { IconEyeSearch, IconSearch, IconX } from "@tabler/icons-react";
import { getListProductAddOnItemID } from "../../../../api/ApiProductAddOn";
import { _localization } from "../../../../config/location";
import { useProductCreateStore } from "../../../../store/useProductCreateStore";
import OpenProductTab from "../../../../components/ProductTabs";
import ButtonClose from "../../../../common/ButtonClose/ButtonClose";
import { useNavigate } from "react-router-dom";

const ItemProductAddonItem = ({ id }: any) => {
  const navigate = useNavigate();
  const dataItemBase = useProductCreateStore((state) => state.dataItemBase);
  const hacomUrl = process.env.REACT_APP_BASE_WEB_HACOM_URL;
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [visible, { toggle, close, open }] = useDisclosure(false);

  const sizeGrid = { base: 6, sm: 4, md: 3, lg: 3, xl: 2 };

  // data tất cả dữ liệu thương hiệu sản phẩm
  const [dataBrand, setDataBrand] = useState<any[]>();

  //   format tiền
  const formatCurrencyVND = (value: any) => {
    if (value == null) {
      return "";
    }
    // Định dạng số theo chuẩn Việt Nam
    return value.toLocaleString("vi-VN", {
      minimumFractionDigits: 0, // Không hiển thị phần thập phân
      maximumFractionDigits: 0, // Đảm bảo không có chữ số sau dấu phẩy
    });
  };

  const [searchValue, setSearchValue] = useDebouncedState("", 300); // Giá trị tìm kiếm gốc

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        Cell: ({ row }) => <>{row?.index + 1}</>,
        size: 50,
      },
      {
        accessorKey: "itemCode",
        header: "Mã sản phẩm",
        Cell: ({ row }) => (
          <Badge color="green" variant="dot" radius="sm" size="lg" px="sm">
            <Text fw={500} size="sm">
              {row.original.itemCode}
            </Text>
          </Badge>
        ),
      },
      {
        accessorKey: "itemName",
        header: "Tên sản phẩm",
        size: 300,
      },
      {
        accessorKey: "warrantyPeriod",
        header: "Thời gian bảo hành",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
      },
      {
        accessorKey: "price",
        header: "Giá gốc",
        Cell: ({ renderedCellValue }: any) =>
          renderedCellValue !== null ? (
            <Text>{formatCurrencyVND(renderedCellValue)} đ</Text>
          ) : (
            ""
          ),
      },
      {
        accessorKey: "fromPrice",
        header: "Giá từ",
        Cell: ({ renderedCellValue }: any) => (
          <>{formatCurrencyVND(renderedCellValue)}</>
        ),
      },
      {
        accessorKey: "toPrice",
        header: "Giá đến",
        Cell: ({ renderedCellValue }: any) => (
          <>{formatCurrencyVND(renderedCellValue)}</>
        ),
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        Cell: ({ renderedCellValue }) => (
          <Badge
            color={renderedCellValue === true ? "green" : "red"}
            variant="filled"
          >
            {renderedCellValue === true ? "Hiển thị" : "Không hiển thị"}
          </Badge>
        ),
      },
      {
        accessorKey: "linkForCustomer",
        header: "Link cho khách hàng",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 200,
      },
      {
        accessorKey: "categoryIds",
        header: "Danh mục áp dụng",
      },
      {
        accessorKey: "Note",
        header: "Ghi chú",
      },
    ],
    [data]
  );

  const table = useMantineReactTable({
    columns,
    data: data || [],
    // enableColumnResizing: true,
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,

    initialState: {
      showColumnFilters: false,
      // columnPinning: {
      //   left: ["STT", "itemCode"],
      //   right: ["action"],
      // },
      // columnVisibility: { id: true },
      density: "xs",
    },
    enableStickyFooter: true,
    enableRowSelection: false,
    enableToolbarInternalActions: false,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    rowCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: 550,
        fontSize: "12px",
        padding: "6px 8px",
      },
    }),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      sorting,
      rowSelection,
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });

  //lấy data list sp
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    open();
    try {
      let url = `?ItemAddOnId=${id}`;

      if (searchValue.length > 0) {
        url += `&KeySearch=${searchValue}`;
      }

      const response = await getListProductAddOnItemID(url);
      if (response) {
        setData(response.data);
        setRowCount(response.totalCount);
      }
      table.resetRowSelection();
    } catch (error: any) {
      setData([]);
      return null;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      close();
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (225 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);
  const onNavigateOnWeb = () => {
    window.open(`${hacomUrl}/${dataItemBase?.url}`, "_blank");
  };
  return (
    <Box
      className="flex-none"
      mx="10px"
      mt={10}
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <Flex justify={"space-between"} align={"center"} my={10}>
        <CopyButton value={String(dataItemBase?.itemName)} timeout={2000}>
          {({ copied, copy }) => (
            <Tooltip
              label={copied ? "Đã sao chép" : "Sao chép"}
              withArrow
              position="right"
            >
              <Badge
                color={copied ? "teal" : "blue"}
                onClick={copy}
                style={{ cursor: "pointer" }}
                size="lg"
              >
                {dataItemBase?.itemName}
              </Badge>
            </Tooltip>
          )}
        </CopyButton>
      </Flex>
      <Flex
        gap="md"
        justify={"space-between"}
        align="center"
        direction="row"
        wrap="wrap"
        w={"100%"}
      ></Flex>

      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "10px 10px",
          overflowX: "hidden",
          overflowY: "hidden",
        }}
        ref={headerRef}
      >
        <Group
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Grid style={{ flexGrow: 1 }}>
            <Grid.Col style={{ display: "flex", gap: "10px" }}>
              <TextInput
                placeholder="Từ khoá"
                type="text"
                leftSection={<IconSearch color="#15aabf" />}
                defaultValue={searchValue}
                onChange={(e) => setSearchValue(e.currentTarget.value)}
              />
              <Button
                type="submit"
                leftSection={<IconSearch size={20} />}
                color="blue"
                variant="outline"
                onClick={async () => {
                  await fetchData();
                }}
              >
                Tìm kiếm
              </Button>
            </Grid.Col>
          </Grid>
          <Button
            type="button"
            color="#3598dc"
            leftSection={<IconEyeSearch size={18} />}
            onClick={() => onNavigateOnWeb()}
          >
            Xem trên web
          </Button>
          <OpenProductTab
            productId={dataItemBase?.id}
            itemCode={dataItemBase?.itemName ?? ""}
            tabToOpen="info10"
            buttonText="Mở trang tab"
          />
          <Button
            color="red"
            onClick={() => {
              navigate(-1);
            }}
            leftSection={<IconX size={18} />}
          >
            Đóng
          </Button>
        </Group>
      </div>
      <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>
    </Box>
  );
};

export default ItemProductAddonItem;
